import { Vms } from '_lib/api';
import applicationReducer from '_store/application/reducer';
import { dialogSelector, selectionSelector } from '_store/application/selectors';
import { DialogType } from '_store/application/types';
import { isVmsWorking, pmvStatusIcon } from '_store/pmv/utils';
import L, { Map } from 'leaflet';
import React, { FC, ReactElement, useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';

import { canInfoViewBeOpened } from '../../../InfoView/utils';
import { encodeClusterMeta, pmvClusterStatus, useClusterRefresh } from '../../clustering';
import PmvInfoTooltip from '../../InfoTooltips/PmvInfoTooltip';
import { HoveringElementType } from '../../InfoTooltips/types';
import { iconSizePx, toLatLng } from '../../utils';

const iconSize = new L.Point(iconSizePx, iconSizePx);
const iconAnchor = new L.Point(iconSize.x / 2, iconSize.y);
const tooltipOffset = new L.Point(0, -iconSize.y);

const PmvMarker: FC<{
  vms: Vms;
  mapRef: Map;
}> = ({ vms, mapRef }): ReactElement => {
  const dispatch = useDispatch();
  const refreshClusters = useClusterRefresh();

  const dialog = useSelector(dialogSelector);
  const selection = useSelector(selectionSelector);
  const canOpenInfo = canInfoViewBeOpened(dialog, selection);

  const markerPosition = useMemo(
    () => toLatLng([vms.longitude, vms.latitude]),
    [vms.longitude, vms.latitude],
  );

  const eventHandlers = useMemo(
    () => ({
      click: () => {
        if (canOpenInfo) {
          dispatch(
            applicationReducer.actions.infoSelected({
              type: DialogType.SINGLE_PMV,
              id: vms.id,
            }),
          );
          mapRef.panTo(markerPosition);
        }
      },
    }),
    [mapRef, vms.id, markerPosition, canOpenInfo, dispatch],
  );

  const isPmvSelected =
    dialog &&
    [DialogType.SINGLE_PMV, DialogType.PMV_PATH].includes(dialog.type) &&
    dialog.id === vms.id;

  const working = isVmsWorking(vms);
  const clusterAttribution = encodeClusterMeta({
    group: 'pmv',
    status: pmvClusterStatus(working),
  });

  const skipFirstRefresh = useRef(true);
  useEffect(() => {
    if (skipFirstRefresh.current) {
      skipFirstRefresh.current = false;
      return;
    }
    refreshClusters();
  }, [clusterAttribution, refreshClusters]);

  return (
    <Marker
      position={markerPosition}
      attribution={clusterAttribution}
      icon={
        new L.Icon({
          iconSize,
          iconUrl: pmvStatusIcon(working),
          iconAnchor,
          className: `marker-event-${isPmvSelected ? 'selected' : 'unselected'}`,
        })
      }
      interactive
      eventHandlers={eventHandlers}
    >
      <PmvInfoTooltip
        hoveringElement={{
          type: HoveringElementType.PMV,
          vms,
          offset: tooltipOffset,
        }}
      />
    </Marker>
  );
};

export default React.memo(PmvMarker);
